import React, { useEffect } from 'react';

import { ColorPicker, Form } from 'antd';
import styled from 'styled-components';

import Input from 'components/lib/Input';
import Radio from 'components/lib/Radio';
import { CommunityPreferenceType } from 'types/communities';

import PreferedColorPicker from './PreferedColorPicker';
import { SPREADSHEET_EXPORT_COLOR_MODE, SPREADSHEET_PREDEFINED_COLORS } from '../../constants';
import SettingsModal from '../../SettingsModal';

const Container = styled.div`
  display: flex;
  flex-flow: column;
  height: 300px;
  padding-top: 16px;
`;

const Title = styled.div`
  font-weight: 900;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  margin-top: 5px;
`;

const StyledFormItem = styled(Form.Item)`
  flex-grow: 1;

  .ant-form-item-row {
    width: 120px;
  }
`;

const StyledRadio = styled(Radio)`
  padding-top: 15px;
`;

type Props = {
  isOpen: boolean;
  loading: boolean;
  communitiesToModify: CommunityPreferenceType[];
  onClose: () => void;
  onSave: (newColor: string) => void;
};

const SpreadsheetExportModal = ({ isOpen, loading, communitiesToModify, onClose, onSave }: Props) => {
  const [form] = Form.useForm();

  const mode = Form.useWatch('mode', form);
  const colorValue = Form.useWatch('color', form);

  useEffect(() => {
    if (!communitiesToModify.length) {
      form.setFieldsValue({
        mode: SPREADSHEET_EXPORT_COLOR_MODE.PREDEFINED_COLORS,
        color: '',
      });
      return;
    }

    let color, mode;

    if (communitiesToModify.length === 1) {
      const communitySpreadsheetColor = communitiesToModify[0].preferences?.spreadsheet_export_color;
      if (communitySpreadsheetColor) {
        color = communitySpreadsheetColor;
        mode = Object.values(SPREADSHEET_PREDEFINED_COLORS).includes(color.toUpperCase())
          ? SPREADSHEET_EXPORT_COLOR_MODE.PREDEFINED_COLORS
          : SPREADSHEET_EXPORT_COLOR_MODE.CUSTOM_COLOR;
      } else {
        color = SPREADSHEET_PREDEFINED_COLORS.BLUE;
        mode = SPREADSHEET_EXPORT_COLOR_MODE.PREDEFINED_COLORS;
      }
    } else {
      mode = SPREADSHEET_EXPORT_COLOR_MODE.PREDEFINED_COLORS;
      color = SPREADSHEET_PREDEFINED_COLORS.PURPLE;
    }
    form.setFieldsValue({ mode, color });
  }, [communitiesToModify, form]);

  const onFinish = async (values: { mode: string; color: string }) => {
    const { color } = values;
    if (!color || communitiesToModify.length === 0) return;

    onSave(color);
  };

  const initialValues = {
    mode: SPREADSHEET_EXPORT_COLOR_MODE.PREDEFINED_COLORS,
    color: '',
  };

  return (
    <SettingsModal
      title="Spreadsheet Export"
      isOpen={isOpen}
      communitiesToModify={communitiesToModify}
      disabled={loading}
      onClose={onClose}
      onSave={() => form.submit()}
      width={540}
    >
      <Container>
        <Title>Export Theme Color</Title>
        <div>
          Select a primary color to style your spreadsheet exports. This color will be used for key highlights and
          headers.
        </div>
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={initialValues}>
          <Form.Item name="mode" noStyle>
            <Radio.Group style={{ display: 'none' }} />
          </Form.Item>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <StyledRadio
              className={'large'}
              checked={mode === SPREADSHEET_EXPORT_COLOR_MODE.PREDEFINED_COLORS}
              onChange={() => form.setFieldsValue({ mode: SPREADSHEET_EXPORT_COLOR_MODE.PREDEFINED_COLORS })}
            >
              Predefined colors
            </StyledRadio>

            {mode === SPREADSHEET_EXPORT_COLOR_MODE.PREDEFINED_COLORS && (
              <Row>
                <Title style={{ paddingTop: 7 }}>Color</Title>
                <div>
                  <PreferedColorPicker
                    color={colorValue}
                    onSelectColor={(c: string) => form.setFieldsValue({ color: c })}
                  />
                  <Form.Item
                    name="color"
                    rules={[{ required: true, message: 'Please select a color' }]}
                    style={{ display: 'none' }}
                  >
                    <Input />
                  </Form.Item>
                </div>
              </Row>
            )}

            <StyledRadio
              className={'large'}
              checked={mode === SPREADSHEET_EXPORT_COLOR_MODE.CUSTOM_COLOR}
              onChange={() => form.setFieldsValue({ mode: SPREADSHEET_EXPORT_COLOR_MODE.CUSTOM_COLOR })}
            >
              Custom color
            </StyledRadio>

            {mode === SPREADSHEET_EXPORT_COLOR_MODE.CUSTOM_COLOR && (
              <Row>
                <Title style={{ paddingTop: 7 }}>Color</Title>
                <StyledFormItem
                  name="color"
                  rules={[{ required: true, message: 'Please pick a color' }]}
                  valuePropName="value"
                  getValueFromEvent={(color) => color.toHexString()}
                >
                  <ColorPicker
                    style={{ height: 36, flexGrow: 1 }}
                    showText={(color) => color.toHexString()}
                    disabledAlpha
                  />
                </StyledFormItem>
              </Row>
            )}
          </div>
        </Form>
      </Container>
    </SettingsModal>
  );
};

export default SpreadsheetExportModal;
